import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { formatISO } from 'date-fns';
import { useMemo } from 'react';
import { Pressable, View, ViewProps } from 'react-native';
import { WorkoutItem } from '~/workout/components/workout-item/WorkoutItem';
import { useCurrentOverviewDate } from '~/calendar/hooks/useCurrentOverviewDate';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { PlannedDateRange } from '@/lib/local-api/workout/planned-date-range';
import type { WorkoutRecord } from '~/powersync/generated/schema';
import { WorkoutCompletionStatus, WorkoutUtils } from '@fytness/shared';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { useWorkoutDuplicate } from '~/workout/hooks/useWorkoutDuplicate';

export interface WorkoutsOverviewSectionProps extends ViewProps {}

export const WorkoutsOverviewSection = ({
  className,
  ...props
}: WorkoutsOverviewSectionProps) => {
  const { t } = useTranslation('workout');
  const router = useRouter();
  const selectedDate = useCurrentOverviewDate();
  const { dispatchDuplicateDialog } = useWorkoutDuplicate();

  // `plannedAt` is a local calendar date; compare it as one (see PlannedDateRange).
  const { fromDate, toDate } = PlannedDateRange.forSingleDay(selectedDate);

  const {
    data: workouts,
    isLoading: loading,
    error,
  } = useLivePowerSyncQuery<WorkoutRecord & { exerciseCount: number }>(
    db.Workout.queries.findByDateRange(fromDate, toDate),
    [fromDate, toDate],
  );

  const isEmpty = !loading && workouts.length === 0;

  const completedCount = useMemo(
    () =>
      workouts.filter(
        (w) =>
          WorkoutUtils.getCompletionStatus(w) ===
          WorkoutCompletionStatus.FINISHED,
      ).length,
    [workouts],
  );

  const handleCreateWorkout = () => {
    router.push({
      pathname: '/workouts/create',
      params: {
        initialDate: formatISO(selectedDate, { representation: 'date' }),
      },
    });
  };

  const handleWorkoutPress = (
    workout: WorkoutRecord & { exerciseCount: number },
  ) => {
    const navigationPath = WorkoutUtils.getNavigationPath(workout);
    router.push({ pathname: navigationPath });
  };

  return (
    <View className={className} {...props}>
      <View className="flex flex-row items-center justify-between mb-3">
        <View className="flex flex-row items-center gap-3">
          <View className="h-0.5 w-6 rounded-sm bg-accent" />
          <Text className="font-inter-bold text-xs tracking-widest text-text-secondary uppercase">
            {t('title')}
          </Text>
        </View>
        {workouts.length > 0 && (
          <View className="rounded bg-accent-soft px-2 py-1">
            <Text
              className="font-inter-bold text-accent"
              style={{ fontSize: 11 }}
            >
              {t('progressCount', {
                completed: completedCount,
                total: workouts.length,
              })}
            </Text>
          </View>
        )}
      </View>

      {isEmpty ? (
        <View className="flex flex-col items-center gap-4 rounded-xl bg-surface p-6 native:p-8">
          <View className="flex items-center justify-center rounded-full bg-accent-soft h-20 w-20">
            <Icon name="Dumbbell" size={32} className="text-accent" />
          </View>

          <Text
            className="font-sora-semibold text-lg text-text-primary"
            style={{ letterSpacing: -0.3 }}
          >
            {t('empty.title')}
          </Text>

          <Text
            className="font-inter-regular text-text-secondary text-center leading-6"
            style={{ fontSize: 13 }}
          >
            {t('empty.description')}
          </Text>

          <Pressable
            testID="workout-add-button"
            onPress={handleCreateWorkout}
            className="flex flex-row items-center justify-center gap-2 rounded-xl bg-accent py-3.5 native:py-4 px-8 w-full active:opacity-90"
          >
            <Icon name="Plus" size={18} className="text-accent-text" />
            <Text className="font-inter-semibold text-sm text-accent-text">
              {t('createWorkout')}
            </Text>
          </Pressable>
        </View>
      ) : (
        <View className="flex flex-col gap-3">
          {workouts.map((workout) => (
            <WorkoutItem
              key={workout.id}
              {...workout}
              onPress={() => handleWorkoutPress(workout)}
              onDuplicate={() => dispatchDuplicateDialog(workout)}
            />
          ))}

          <Pressable
            testID="workout-add-button"
            onPress={handleCreateWorkout}
            className="flex flex-row items-center justify-center gap-2 py-3"
          >
            <Icon name="Plus" size={16} className="text-text-muted" />
            <Text
              className="font-inter-medium text-text-muted"
              style={{ fontSize: 13 }}
            >
              {t('createWorkout')}
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
};
